import { useEffect, useRef } from 'react';

const SESSION_STORAGE_KEY = 'app_session_token';
const USER_STORAGE_KEY = 'app_user_data';
const SESSION_EXPIRY_TIME = 15 * 60 * 1000; // 15 minutes
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes of inactivity
const VALIDATION_INTERVAL = 1 * 60 * 1000; // Check every minute

export function useSessionManager() {
  const inactivityTimerRef = useRef(null);
  const validationTimerRef = useRef(null);

  // Reset inactivity timer on user action
  const resetInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      // Inactivity timeout reached
      logoutUser('Session expired due to inactivity');
    }, INACTIVITY_TIMEOUT);
  };

  // Validate session with server
  const validateSession = async () => {
    const sessionToken = localStorage.getItem(SESSION_STORAGE_KEY);
    
    if (!sessionToken) {
      return false;
    }

    try {
      const response = await fetch('/api/validate-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': sessionToken,
        },
        body: JSON.stringify({ sessionToken }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.log('❌ Session validation failed');
        logoutUser('Session expired or invalid');
        return false;
      }

      console.log('✅ Session is valid');
      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  };

  // Logout user
  const logoutUser = async (reason = 'User logged out') => {
    try {
      const sessionToken = localStorage.getItem(SESSION_STORAGE_KEY);
      const userData = JSON.parse(localStorage.getItem(USER_STORAGE_KEY) || '{}');

      // Call logout API
      if (sessionToken) {
        await fetch('/api/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Token': sessionToken,
          },
          body: JSON.stringify({ sessionToken }),
        }).catch(err => console.error('Logout API error:', err));
      }

      // Log the action
      if (userData?.id) {
        await fetch('/api/log-action', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userData.id,
            userEmail: userData.email,
            action: 'LOGOUT',
            description: reason,
            timestamp: new Date().toISOString(),
          }),
        }).catch(err => console.error('Log action error:', err));
      }

      // Clear storage
      localStorage.clear();
      sessionStorage.clear();

      // Clear caches
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            caches.delete(cacheName).catch(err => console.error('Cache clear error:', err));
          });
        }).catch(err => console.error('Caches access error:', err));
      }

      // Redirect to login
      const message = reason === 'Session expired due to inactivity' 
        ? 'Your session expired due to inactivity. Please login again.'
        : reason;
      window.location.href = `/login?message=${encodeURIComponent(message)}`;
    } catch (err) {
      console.error('Logout error:', err);
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  };

  // Setup session management on mount
  useEffect(() => {
    const sessionToken = localStorage.getItem(SESSION_STORAGE_KEY);
    
    if (!sessionToken) {
      return; // No active session
    }

    console.log('📱 Session manager initialized');

    // Start inactivity timer
    resetInactivityTimer();

    // Start session validation timer
    validationTimerRef.current = setInterval(() => {
      validateSession();
    }, VALIDATION_INTERVAL);

    // Add event listeners for user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, true);
    });

    // Cleanup
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (validationTimerRef.current) {
        clearInterval(validationTimerRef.current);
      }

      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer, true);
      });
    };
  }, []);

  return {
    validateSession,
    logoutUser,
    resetInactivityTimer,
  };
}
